import React, { useContext } from 'react';
import { Box, Stack, Typography, Alert, Card } from '@mui/material';
import { translate as t } from 'hooks/useTranslations';
import { AppContext } from 'views/App/context';
import { ComplianceSummary } from './ComplianceSummary';
import { ExpiringCredentials } from './ExpiringCredentials';
import { InsightsMetrics } from './InsightsMetrics';
import { useUrlState } from 'hooks/useUrlState';
import { ReportingPeriodFilter } from './_types';
import { TrainingProvider } from './TrainingContext';
import { ExportReportMenu } from './ExportReportMenu';

const TrainingContent: React.FC = () => {
  const { hasNoPermission, hasAppPermission, hasPermissionError } = useContext(AppContext);

  if (hasPermissionError) {
    return (
      <Alert
        severity='error'
        sx={{ mb: 2 }}
      >
        <Typography variant='body1'>
          Lexipol has encountered a problem retrieving your data, please contact Customer Support.
        </Typography>
      </Alert>
    );
  }

  if (hasNoPermission() && !hasPermissionError) {
    return (
      <Alert severity='warning'>
        <Typography variant='body1'>{t('dashboard.noPermissions')}</Typography>
      </Alert>
    );
  }

  return (
    <Box className='training-dashboard'>
      {/* Header with Export Menu */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          mb: 4,
        }}
      >
        <ExportReportMenu />
      </Box>

      <Stack
        spacing={8}
        sx={{ minWidth: 0, mb: '16px' }}
      >
        <InsightsMetrics />

        {/* Charts Section with View Reports Header */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { sm: '1fr 2fr', xs: 'repeat(1, 1fr)' }, gap: 4 }}>
          <ComplianceSummary />
          <ExpiringCredentials />
        </Box>
      </Stack>
    </Box>
  );
};

const Training: React.FC = () => {
  const { value: currentPeriodFilter } = useUrlState('period', ReportingPeriodFilter.PREVIOUS_MONTH);

  return (
    <TrainingProvider periodFilter={currentPeriodFilter}>
      <TrainingContent />
    </TrainingProvider>
  );
};

export default Training;
